import { Dispatch, FC, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { Download, Search, X } from "lucide-react";
import {
  dashboardDataTable as dt,
  DashboardDataTableScroll,
  DashboardDataTableSortButton,
  DashboardDataTableTable,
  TablePagePagination,
  toggleTableSort,
  DefinitionTooltip,
  type DefinitionTooltipInfo,
  type SortDirection,
} from "src/components/common";
import LoadingSpinner from "src/components/reusable/LoadingSpinner";
import StyledDropdown from "src/components/reusable/StyledDropdown";
import type { CustomerAnalyticsRow } from "src/services/CLTVDashboardAPI";
import {
  formatMoneyCell,
  getCustomerLifetimeLabel,
  getCustomerSegmentLabel,
  getPredictedOrders,
  getTotalOrders,
  getTotalSpend,
  SEGMENT_PILL_FILTERS,
  type CustomerSegmentTone,
  type CustomerTableSortKey,
} from "./CltvDashboardShared";
import "../Styles/CltvCustomerAnalyticsTable.scss";

type CltvCustomerAnalyticsTableProps = {
  isAr: boolean;
  customerTableTooltip?: DefinitionTooltipInfo;
  columnTooltips: {
    profile?: DefinitionTooltipInfo;
    totalSpend?: DefinitionTooltipInfo;
    totalOrders?: DefinitionTooltipInfo;
    predictedSpend?: DefinitionTooltipInfo;
    predictedOrders?: DefinitionTooltipInfo;
    segments?: DefinitionTooltipInfo;
  };
  searchInput: string;
  onSearchInputChange: (_value: string) => void;
  selectedSegments: string[];
  onSelectedSegmentsChange: (_value: string[]) => void;
  setTableSort: Dispatch<
    SetStateAction<{ key: CustomerTableSortKey; direction: SortDirection } | null>
  >;
  customerError: string | null;
  customerLoading: boolean;
  exportCustomerRows: CustomerAnalyticsRow[];
  sortedCustomerRowsLength: number;
  isExporting: boolean;
  onExportCustomerCsv: () => void;
  emptyCell: string;
  formatMoneyShort: (_value: number) => string;
  formatCount: (_value: number | null | undefined) => string;
  maxObservedSpend: number;
  segmentTonesForRow: (_row: CustomerAnalyticsRow) => CustomerSegmentTone[];
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  customerTableTotalPages: number;
};

const CltvCustomerAnalyticsTable: FC<CltvCustomerAnalyticsTableProps> = ({
  isAr,
  customerTableTooltip,
  columnTooltips,
  searchInput,
  onSearchInputChange,
  selectedSegments,
  onSelectedSegmentsChange,
  setTableSort,
  customerError,
  customerLoading,
  exportCustomerRows,
  sortedCustomerRowsLength,
  isExporting,
  onExportCustomerCsv,
  emptyCell,
  formatMoneyShort,
  formatCount,
  maxObservedSpend,
  segmentTonesForRow,
  page,
  setPage,
  customerTableTotalPages,
}) => {
  const { t, i18n } = useTranslation();

  return (
    <article
      className="cltv-dashboard__panel cltv-dashboard__panel--customer-analytics"
      data-pdf-block="customer-table"
      data-pdf-splittable
    >
      <div className="cltv-dashboard__customer-analytics">
        <div className="cltv-dashboard__table-toolbar">
          <div className="cltv-dashboard__table-toolbar-left">
            <div className="cltv-dashboard__title-with-tooltip cltv-dashboard__title-with-tooltip--table">
              <h3 className="cltv-dashboard__data-table-title">
                {t("cltv.sections.customerTable")}
              </h3>
              {customerTableTooltip ? (
                <DefinitionTooltip
                  label={t("cltv.sections.customerTable")}
                  info={customerTableTooltip}
                  formulaTitle={t("cltv.common.formula")}
                />
              ) : null}
            </div>
          </div>
          <div className="cltv-dashboard__table-controls">
            <label className="cltv-dashboard__table-search">
              <Search size={15} />
              <input
                type="text"
                value={searchInput}
                placeholder={t("cltv.table.searchPlaceholder")}
                onChange={event => onSearchInputChange(event.target.value)}
              />
              {searchInput && (
                <button
                  type="button"
                  className="cltv-dashboard__table-search-clear"
                  aria-label={t("cltv.table.clearSearch")}
                  onClick={() => onSearchInputChange("")}
                >
                  <X size={14} />
                </button>
              )}
            </label>
            <StyledDropdown
              mode="multiple"
              value={selectedSegments}
              placeholder={t("cltv.allSegments", { defaultValue: "All Segments" })}
              allowClear
              showSearch={false}
              className="filter-dropdown"
              popupClassName="filter-dropdown-menu"
              style={{ minWidth: 180 }}
              onChange={(value: string[]) => onSelectedSegmentsChange(value ?? [])}
              options={SEGMENT_PILL_FILTERS.map(pill => ({
                value: pill.key,
                label: t(pill.labelKey, { defaultValue: pill.key }),
              }))}
            />
            <button
              type="button"
              className="cltv-dashboard__export-button"
              data-pdf-ignore
              onClick={onExportCustomerCsv}
              disabled={sortedCustomerRowsLength === 0 || isExporting}
            >
              <Download size={16} />
              {isExporting ? t("cltv.table.exporting") : t("cltv.table.export")}
            </button>
          </div>
        </div>

        {customerError ? (
          <p className="cltv-dashboard__error-banner" role="alert">
            {customerError}
          </p>
        ) : null}

        <DashboardDataTableScroll className="cltv-dashboard__table-wrap">
        {customerLoading ? (
          <LoadingSpinner compact />
        ) : (
          <DashboardDataTableTable className="cltv-dashboard__customer-table">
            <thead>
              <tr>
                <th>
                  <DashboardDataTableSortButton onClick={() => setTableSort(current => toggleTableSort(current, "customerName"))}>
                    {t("cltv.headers.customerName")}
                  </DashboardDataTableSortButton>
                </th>
                <th>
                  <DashboardDataTableSortButton onClick={() => setTableSort(current => toggleTableSort(current, "email"))}>
                    {t("cltv.headers.customerEmail")}
                  </DashboardDataTableSortButton>
                </th>
                <th>
                  <span className={dt.thHelpRow}>
                    <DashboardDataTableSortButton onClick={() => setTableSort(current => toggleTableSort(current, "profile"))}>
                      {t("cltv.headers.customerProfile")}
                    </DashboardDataTableSortButton>
                    {columnTooltips.profile ? (
                      <DefinitionTooltip
                        label={t("cltv.headers.customerProfile")}
                        info={columnTooltips.profile}
                        formulaTitle={t("cltv.common.formula")}
                      />
                    ) : null}
                  </span>
                </th>
                <th className={dt.number}>
                  <span className={dt.thHelpRow}>
                    <DashboardDataTableSortButton onClick={() => setTableSort(current => toggleTableSort(current, "totalSpend"))}>
                      {t("cltv.headers.totalSpend")}
                    </DashboardDataTableSortButton>
                    {columnTooltips.totalSpend ? (
                      <DefinitionTooltip
                        label={t("cltv.headers.totalSpend")}
                        info={columnTooltips.totalSpend}
                        formulaTitle={t("cltv.common.formula")}
                      />
                    ) : null}
                  </span>
                </th>
                <th className={dt.number}>
                  <span className={dt.thHelpRow}>
                    <DashboardDataTableSortButton onClick={() => setTableSort(current => toggleTableSort(current, "totalOrders"))}>
                      {t("cltv.headers.totalOrders")}
                    </DashboardDataTableSortButton>
                    {columnTooltips.totalOrders ? (
                      <DefinitionTooltip
                        label={t("cltv.headers.totalOrders")}
                        info={columnTooltips.totalOrders}
                        formulaTitle={t("cltv.common.formula")}
                      />
                    ) : null}
                  </span>
                </th>
                <th className={dt.number}>
                  <span className={dt.thHelpRow}>
                    <DashboardDataTableSortButton onClick={() => setTableSort(current => toggleTableSort(current, "predictedSpend"))}>
                      {t("cltv.headers.predictedSpend3m")}
                    </DashboardDataTableSortButton>
                    {columnTooltips.predictedSpend ? (
                      <DefinitionTooltip
                        label={t("cltv.headers.predictedSpend3m")}
                        info={columnTooltips.predictedSpend}
                        formulaTitle={t("cltv.common.formula")}
                        align="end"
                      />
                    ) : null}
                  </span>
                </th>
                <th className={dt.number}>
                  <span className={dt.thHelpRow}>
                    <DashboardDataTableSortButton onClick={() => setTableSort(current => toggleTableSort(current, "predictedOrders"))}>
                      {t("cltv.headers.predictedOrders3m")}
                    </DashboardDataTableSortButton>
                    {columnTooltips.predictedOrders ? (
                      <DefinitionTooltip
                        label={t("cltv.headers.predictedOrders3m")}
                        info={columnTooltips.predictedOrders}
                        formulaTitle={t("cltv.common.formula")}
                        align="end"
                      />
                    ) : null}
                  </span>
                </th>
                <th>
                  <span className={dt.thHelpRow}>
                    {t("cltv.headers.segments")}
                    {columnTooltips.segments ? (
                      <DefinitionTooltip
                        label={t("cltv.headers.segments")}
                        info={columnTooltips.segments}
                        formulaTitle={t("cltv.common.formula")}
                        align="end"
                      />
                    ) : null}
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {exportCustomerRows.map(row => {
                const totalOrders = getTotalOrders(row);
                const totalSpend = getTotalSpend(row);
                const predictedOrders = getPredictedOrders(row);
                const monthsActive = Math.max(1, row.customerAgeMonths ?? totalOrders * 6);
                const profileStrength = Math.min(100, Math.round((totalSpend / maxObservedSpend) * 100));
                const segments = segmentTonesForRow(row);

                return (
                  <tr key={`${row.customerId}-${row.email}`}>
                    <td className="cltv-dashboard__customer-name">
                      {row.customerName || row.customerId || <span className={dt.empty}>{emptyCell}</span>}
                    </td>
                    <td className="cltv-dashboard__email">
                      {row.email ? row.email : <span className={dt.empty}>{emptyCell}</span>}
                    </td>
                    <td>
                      <div className="cltv-dashboard__profile-cell">
                        <strong>{getCustomerLifetimeLabel(monthsActive, t, i18n.language)}</strong>
                        <span className="cltv-dashboard__profile-subtext">
                          {t("cltv.table.observedInWindows")}
                        </span>
                        <div className="cltv-dashboard__profile-meter">
                          <span style={{ width: `${profileStrength}%` }} />
                        </div>
                      </div>
                    </td>
                    <td className={`${dt.number} cltv-dashboard__numeric`}>
                      {formatMoneyCell(row.totalSpendFormattedValue, row.totalSpendSince2023, formatMoneyShort) ?? <span className={dt.empty}>{emptyCell}</span>}
                    </td>
                    <td className={`${dt.number} cltv-dashboard__numeric`}>
                      {row.totalOrdersSince2023 !== null && row.totalOrdersSince2023 !== undefined ? formatCount(totalOrders) : <span className={dt.empty}>{emptyCell}</span>}
                    </td>
                    <td className={`${dt.number} cltv-dashboard__numeric`}>
                      {formatMoneyCell(row.predictedSpend3mFormattedValue, row.predictedSpend3m, formatMoneyShort) ?? <span className={dt.empty}>{emptyCell}</span>}
                    </td>
                    <td className={`${dt.number} cltv-dashboard__numeric`}>
                      {row.predictedOrders3m !== null && row.predictedOrders3m !== undefined ? formatCount(predictedOrders) : <span className={dt.empty}>{emptyCell}</span>}
                    </td>
                    <td className="cltv-dashboard__segments-cell">
                      {segments.length > 0 ? (
                        <div className="cltv-dashboard__segment-badges">
                          {segments.slice(0, 2).map(segment => (
                            <span
                              key={`${row.customerId}-${segment}`}
                              className={`cltv-dashboard__segment-pill cltv-dashboard__segment-pill--${segment}`}
                            >
                              {getCustomerSegmentLabel(segment, t)}
                            </span>
                          ))}
                          {segments.length > 2 && (
                            <span className="cltv-dashboard__segment-pill cltv-dashboard__segment-pill--overflow">
                              +{formatCount(segments.length - 2)}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className={dt.empty}>{emptyCell}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </DashboardDataTableTable>
        )}
        </DashboardDataTableScroll>

        {!customerError && !customerLoading ? (
          <div
            className="cltv-dashboard__table-pagination"
            dir={isAr ? "rtl" : "ltr"}
          >
            <TablePagePagination
              currentPage={page}
              totalPages={customerTableTotalPages}
              onPrevious={() => setPage(p => Math.max(1, p - 1))}
              onNext={() => setPage(p => Math.min(customerTableTotalPages, p + 1))}
            />
          </div>
        ) : null}
      </div>
    </article>
  );
};

export default CltvCustomerAnalyticsTable;
